<template>
<div class="main user-permissions">

    <h1>User Permissions <i class="fa fa-chevron-right" /> {{ selectedUserTypeCode ? userTypeDescription : fullName }}</h1>

    <div class="list-view with-sidebar no-sub-header">

        <div class="filters" id="user-permission-filters">
            <div class="title" style="font-size: 1em; margin: 0 0 1em 0;">User types...</div>
            <div style="position: relative;">
    			<div v-for="(userType, index) in managerUserTypes" :class="['filter-list-button-large', { active: selectedUserTypeCode == userType.user_type_code }, { disabled: saveEnabled }]" @click="setSelectedUserTypeCode(userType.user_type_code)" :key="index">{{ userType.description }}</div>
                <div v-if="selectedUser.user_id">
                    <div class="divider" style="margin: 0.5em 0 1em 0;"></div>
                    <div class="title" style="font-size: 1em; margin: 1em 0;">User...</div>
                    <div :class="[ 'filter-list-button-large', 'user', { active: !selectedUserTypeCode }, { disabled: saveEnabled } ]" @click="fetchUserPermissions()">{{ fullName }}</div>
                </div>
                <div v-if="selectNotAllowed" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; font-size: 1.25em; padding: 6em 0; text-align: center; background: rgba(255,255,255,.7);">
                    Save or cancel<br>your changes<br>before selecting<br>another type.
                </div>
            </div>

            <div class="divider" style="margin: 0.5em 0 1em 0;"></div>

            <div class="title" style="font-size: 1em; margin: 1em 0;">Need to set permissions for a specific user?</div>

            <div class="input-with-control">
				<input type="text" v-model="userSearchTerm" placeholder="Search users..." @keyup.enter="searchUsers()">
				<div class="button action input-go" @click="searchUsers()"><i class="fas fa-search"></i></div>
			</div>
            <ul class="user-list" v-if="users.length > 0">
                <li v-for="(user, index) in users" @click="fetchUserPermissions(index)" :key="index">{{ user.full_name }}</li>
            </ul>

            <div class="divider" style="margin: 0.5em 0 1em 0;"></div>

            <div class="button action" @click="saveUserPermissions()" v-if="saveEnabled && !selectedUserTypeCode">Save</div>
            <div class="button action" @click="saveUserTypePermissions()" v-else-if="saveEnabled && selectedUserTypeCode">Save</div>
            <div class="button disabled" v-else>Save Permissions</div>
            <div class="button cancel" @click="cancelUserTypePermissions()" v-if="saveEnabled">Cancel</div>
            <div class="button disabled" v-else>Cancel</div>
            <div class="button neutral" @click="resetPermissions()">Reset Defaults</div>
			<div class="button action" @click="showModal = true">Add a User Type</div>
            <div class="divider"></div>
            <router-link class="extra-menu-item neutral" to="/users/user-type-group/managers">Manage Users</router-link>
		</div>

        <div class="content user-permission-content">

            <div class="flight-deck-nav-pane" v-if="!getShowUserPermissionsAdvancedSettings">
                <div class="column-common" v-if="Object.keys(menus.flight_deck_meta).length > 0">
                    <template v-for="(menuItem, index) in menus.flight_deck_meta">
                        <div class="pointer" @click="toggleNotAllowed(menuItem.menu_item_id)">
                            <i class="far fa-square" v-if="notAllowed(menuItem.menu_item_id)"></i>
                            <i class="far fa-check-square" v-else></i>
                            <span :class="{ not_allowed: notAllowed(menuItem.menu_item_id) }">{{ menuItem.menu_label }}</span>
                        </div>
                    </template>
                </div>
                <div class="flight-deck-nav-pane-columns">
                    <ul class="columns" v-if="Object.keys(menus.flight_deck_main).length > 0">
                        <li class="flight-deck-nav-columns" v-for="(subMenu, index) in menus.flight_deck_main" :key="'sub-menu-' + index">
                            <img v-bind:src="require('@/assets/img/Icon' + subMenu.menu_label.split(' ')[0] + '.png')" class="icon">
                            <div v-if="subMenu.menu_items && Object.keys(subMenu.menu_items).length > 0">
                                <div class="flight-deck-section-title current-link" @click="toggleNotAllowed(subMenu.menu_item_id, subMenu.menu_items)">
                                    <i class="far fa-square" v-if="notAllowed(subMenu.menu_item_id)"></i>
                                    <i class="far fa-check-square" v-else></i>
                                    <span :class="{ not_allowed: notAllowed(subMenu.menu_item_id) }">{{ subMenu.menu_label }}</span>
                                </div>
                                <template v-for="(menuItem, index) in subMenu.menu_items">
                                    <div class="pointer" @click="toggleNotAllowed(menuItem.menu_item_id || menuItem.menu_label)">
                                        <i class="far fa-square" v-if="notAllowed(menuItem.menu_item_id)"></i>
                                        <i class="far fa-check-square" v-else></i>
                                        <span :class="[{ not_allowed: notAllowed(menuItem.menu_item_id) }]">{{ menuItem.menu_label }}</span>
                                    </div>
                                </template>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="advanced-user-permissions" v-if="getShowUserPermissionsAdvancedSettings">
                <ul class="columns" v-if="Object.keys(menus.flight_deck_main).length > 0">
                    <li class="flight-deck-nav-columns" v-for="(subMenu, index) in menus.flight_deck_main" :key="'sub-menu-' + index">
                        <div v-if="subMenu.menu_items && Object.keys(subMenu.menu_items).length > 0">
                            <div class="title height-23" @click="togglePermissions(subMenu.menu_item_id, subMenu.menu_items)">
                                <i class="enabled fas fa-edit" v-if="!notAllowed(subMenu.menu_item_id) && !readOnly(subMenu.menu_item_id)"></i>
                                <i class="enabled fas fa-eye" v-else-if="readOnly(subMenu.menu_item_id)"></i>
                                <span class="not-allowed" v-else>
                                    <i class="fas fa-eye"></i>
                                    <i class="fas fa-slash"></i>
                                </span>
                                {{ subMenu.menu_label }}
                            </div>
                            <template v-for="(menuItem, index) in subMenu.menu_items">
                                <div class="height-23" @click="togglePermissions(menuItem.menu_item_id)">
                                    <i class="enabled fas fa-edit" v-if="!notAllowed(menuItem.menu_item_id) && !readOnly(menuItem.menu_item_id)"></i>
                                    <i class="enabled fas fa-eye" v-else-if="readOnly(menuItem.menu_item_id)"></i>
                                    <span class="not-allowed" v-else>
                                        <i class="fas fa-eye"></i>
                                        <i class="fas fa-slash"></i>
                                    </span>
                                    <span class="indent-1">{{ menuItem.menu_label }}</span>
                                </div>
                            </template>
                        </div>
                    </li>
                </ul>
            </div>

            <div style="margin: 2em 0 0 0;" v-if="!getShowUserPermissionsAdvancedSettings">
                <h1><span class="type">{{ selectedUserTypeCode ? userTypeDescription : fullName }}</span> Dashboard Tile Permissions</h1>
                <div class="dashboard-flight-deck">
                    <div>
                        <div class="first-row">
                            <div class="controls" @click="toggleNotAllowed(getMenuItemIdFromLabel('POS & Warehouse Sales'))">
                                <i class="far fa-square" v-if="notAllowed(getMenuItemIdFromLabel('POS & Warehouse Sales'))"></i>
                                <i class="far fa-check-square" v-else></i>
                            </div>
                            <div class="dashboard-tile orderssales">
                                <div class="title">Recent Sales</div>
                                <div class="statistic">
                                    <div class="stat">$...</div>
                                    <div class="caption">Last 7 days</div>
                                </div>
                                <a href="#" class="dashboard-link">Manage Orders &amp; Sales</a>
                            </div>
                            <div class="controls" @click="toggleNotAllowed(getMenuItemIdFromLabel('Inventory Management Report'))">
                                <i class="far fa-square" v-if="notAllowed(getMenuItemIdFromLabel('Inventory Management Report'))"></i>
                                <i class="far fa-check-square" v-else></i>
                            </div>
                            <div class="dashboard-tile inventory">
                                <div class="title">Store Inventory</div>
                                <div class="statistic">
                                    <div class="stat">$...</div>
                                    <div class="caption">Current on hand inventory</div>
                                </div>
                                <a href="#" class="dashboard-link">Manage Inventory</a>
                            </div>
                        </div>
                        <div class="second-row">
                            <div class="controls" @click="toggleNotAllowed(getMenuItemIdFromLabel('Search Logbooks'))">
                                <i class="far fa-square" v-if="notAllowed(getMenuItemIdFromLabel('Search Logbooks'))"></i>
                                <i class="far fa-check-square" v-else></i>
                            </div>
                            <div class="dashboard-tile compliance">
                                <div class="title">Logbook Dispositions</div>
                                <div class="statistic">
                                    <div class="stat">...</div>
                                    <div class="caption">Last 5 Business Days</div>
                                </div>
                                <a href="#" class="dashboard-link">Manage Logbooks</a>
                            </div>
                            <div class="controls" @click="toggleNotAllowed(getMenuItemIdFromLabel('Promo Packs'))">
                                <i class="far fa-square" v-if="notAllowed(getMenuItemIdFromLabel('Promo Packs'))"></i>
                                <i class="far fa-check-square" v-else></i>
                            </div>
                            <div class="dashboard-tile promotion">
                                <div class="title">Promo Packs</div>
                                <div class="statistic">
                                    <div class="stat">...</div>
                                    <div class="caption">Currently Available</div>
                                </div>
                                <a href="#" class="dashboard-link">Manage Promo Packs</a>
                            </div>
                            <div class="controls" @click="toggleNotAllowed(getMenuItemIdFromLabel('Customer List'))">
                                <i class="far fa-square" v-if="notAllowed(getMenuItemIdFromLabel('Customer List'))"></i>
                                <i class="far fa-check-square" v-else></i>
                            </div>
                            <div class="dashboard-tile customers">
                                <div class="title">Customers</div>
                                <div class="statistic">
                                    <div class="stat">...</div>
                                    <div class="caption">Current list</div>
                                </div>
                                <a href="#" class="dashboard-link">Manage Customers</a>
                            </div>
                            <div class="controls" @click="toggleNotAllowed(getMenuItemIdFromLabel('Reorder Cart'))">
                                <i class="far fa-square" v-if="notAllowed(getMenuItemIdFromLabel('Reorder Cart'))"></i>
                                <i class="far fa-check-square" v-else></i>
                            </div>
                            <div class="dashboard-tile dashboard">
                                <div class="title">Reorder Cart</div>
                                <div class="statistic">
                                    <div class="stat">...</div>
                                    <div class="caption">In Your Reorder Cart</div>
                                </div>
                                <a href="#" class="dashboard-link">Manage Reorder Cart</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

    </div>

	<div class="modal" v-if="showModal">
		<div class="inner">
            <h2 v-if="savingUserTypePermissions">User Type Permissions</h2>
			<h2 v-else>Add A User Type</h2>
			<div class="content">
                <div v-if="!savingUserTypePermissions">
                    <p>
                        User types allow you to set permissions for more than one user at a time.<br />
                        You can create a new user type here, set various permissions, then assign<br />
                        users to this new user type.
                    </p>
                    <label>User Type Code</label>
                    <input type="text" v-model="newUserType.user_type_code" placeholder="e.g. RECEIVING" style="text-transform:uppercase" />
                    <label>Description</label>
                    <input type="text" v-model="newUserType.description" placeholder="e.g. Receiving clerk" />
                </div>
				<div v-if="modalMessage" class="empty">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel" @click="showModal = false">Cancel</div>
				<div v-if="!savingUserTypePermissions" :class="['button', modalMessage ? 'disabled' : 'action']" @click="saveNewUserType()">Save</div>
			</div>
		</div>
	</div>

</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
export default {
    name: 'user-permissions',
    props: ['user_id'],
    data() {
        return {
            loading: false,
            message: '',
            menus: {
                flight_deck_meta: {},
                flight_deck_main: {}
            },
            selectedUserTypeCode: '',
            selectedUser: {},
            permissionsMode: 'user_type',
            showModal: false,
            modalMessage: '',
            savingUserTypePermissions: false,
            userTypes: [],
            users: [],
            userSearchTerm: '',
            clientMenuItemPolicies: {},
            clientMenuItemPoliciesCopy: {},
            systemMenuItemPolicies: {},
            systemMenuItemPoliciesCopy: {},
            newUserType: {},
            showResetUserTypePermissionsWarning: false,
            selectNotAllowed: false
        }
    },
    mounted() {
        // NOTE: system admins need to acquire the full menu, not just what managers would see
        this.getMenu('FLIGHT_DECK_META')
        this.getMenu('FLIGHT_DECK_MAIN')
        // NOTE: fetch user types, which will then fetch user type permissions for the first type in the list
        this.fetchUserTypes()
        if (this.user_id) {
            this.searchUsers()
        } else {
            this.selectedUserTypeCode = 'MANAGER'
        }
    },
    watch: {
    },
    computed: {
        ...mapGetters('dashboard_module', [
            'getShowUserPermissionsAdvancedSettings'
        ]),
        saveEnabled() {
            if (this.getAdministratorFlag) {
                return this.$changed(this.systemMenuItemPolicies, this.systemMenuItemPoliciesCopy)
            } else {
                return this.$changed(this.clientMenuItemPolicies, this.clientMenuItemPoliciesCopy)
            }
        },
        revertEnabled() {
            return this.$changed(this.clientMenuItemUserTypePolicies, this.systemMenuItemUserTypePolicies) || this.$changed(this.systemMenuItemPolicies, this.systemMenuItemPoliciesCopy)
        },
        fullName() {
            if (!this.selectedUser.user_id) return
            return (this.selectedUser.full_name) ? this.selectedUser.full_name : this.selectedUser.first_name + " " + this.selectedUser.last_name
        },
        userTypeDescription() {
            for (let i = 0; i < this.userTypes.length; i++) {
                if (this.userTypes[i].user_type_code == this.selectedUserTypeCode) {
                    return this.userTypes[i].description
                }
            }
            return ''
        },
		managerUserTypes() {
			return this.userTypes.filter(user_type => user_type.manager_flag == 1);
		}
    },
    methods: {
        ...mapActions('dashboard_module', [
            'toggleShowUserPermissionsAdvancedSettings'
        ]),
        getMenu(menuCode) {
            this.apiCall({ destination: 'get_menu_recursive', data: { menu_code: menuCode } }).then(obj => {
                this.menus[menuCode.toLowerCase()] = obj.menu
            }).catch(err => {
                console.log(err);
            })
        },
        fetchUserTypes() {
            this.userTypes = []
            this.apiCall({ destination: 'get_list_user_types' }).then(obj => {
                // eclude AUDITOR from permissions!
                for (let i = 0; i < obj.user_types.length; i++) {
                    if (obj.user_types[i]['user_type_code'] != 'AUDITOR') {
                        this.userTypes.push(obj.user_types[i])
                    }
                }
                if (!this.user_id) {
                    this.selectedUserTypeCode = this.managerUserTypes[0].user_type_code
                    this.fetchUserTypePermissions()
                }
            }).catch(err => {
                console.log(err);
            })
        },
        fetchUserTypePermissions(clear = false) {
            if (this.saveEnabled || this.loading) { return }
            this.loading = true
            if (clear) {
                this.selectedUser = ''
                this.selectedUserTypeCode = this.userTypes[0].user_type_code
            }
            this.users = []
            this.userSearchTerm = ''
            this.apiCall({ destination: 'get_list_menu_item_user_type_policies', data: { user_type_code: this.selectedUserTypeCode }}).then(obj => {
                this.loading = false
                if (obj.client_menu_item_user_type_policies || obj.system_menu_item_user_type_policies) {
                    this.clientMenuItemPolicies = obj.client_menu_item_user_type_policies
                    this.systemMenuItemPolicies = obj.system_menu_item_user_type_policies
                    this.clientMenuItemPoliciesCopy = this.$copy(obj.client_menu_item_user_type_policies)
                    this.systemMenuItemPoliciesCopy = this.$copy(obj.system_menu_item_user_type_policies)
                }
            }).catch(err => {
                this.loading = false
                console.log(err);
            })
        },
        setSelectedUserTypeCode(userTypecode) {
            this.selectNotAllowed = this.saveEnabled
            if (!this.saveEnabled && this.selectedUserTypeCode != userTypecode) {
                this.selectedUserTypeCode = userTypecode
                this.fetchUserTypePermissions()
            }
        },
        getMenuItemIdFromLabel(label) {
            for (let [key, menuItem] of Object.entries(this.menus.flight_deck_meta)) {
                if (menuItem.menu_label == label) {
                    return menuItem.menu_item_id
                }
            }
            for (let [key, subMenu] of Object.entries(this.menus.flight_deck_main)) {
                for (let [key, menuItem] of Object.entries(subMenu.menu_items)) {
                    if (menuItem.menu_label == label) {
                        return menuItem.menu_item_id
                    }
                }
            }
            return ''
        },
        searchUsers() {
            this.loading = true
            this.apiCall({ destination: 'get_list_users', data: { search_term: this.userSearchTerm, manager_flag: 1, user_id: this.user_id }}).then(obj => {
                this.loading = false
                if (obj.status == 'success' && obj.users.length > 1) {
                    this.users = obj.users
                } else if (obj.users.length == 1) {
                    this.selectedUser = obj.users[0]
                    this.fetchUserPermissions()
                }
            }).catch(err => {
                this.loading = false
                console.log(err);
            })
        },
        fetchUserPermissions(index = null) {
            if (this.saveEnabled || this.loading) { return }
            if (this.loading) return
            this.loading = true
            if (index !== null) this.selectedUser = this.users[index]
            this.users = []
            this.userSearchTerm = ''
            this.selectedUserTypeCode = ''
            this.apiCall({ destination: 'get_list_menu_item_user_policies', data: { user_id: this.selectedUser.user_id, user_type_code: this.selectedUser.user_type_code }}).then(obj => {
                this.loading = false
                if (obj.client_menu_item_user_policies || obj.system_menu_item_user_policies) {
                    this.clientMenuItemPolicies = obj.client_menu_item_user_policies
                    this.systemMenuItemPolicies = obj.system_menu_item_user_policies
                    this.clientMenuItemPoliciesCopy = this.$copy(obj.client_menu_item_user_policies)
                }
            }).catch(err => {
                this.loading = false
                console.log(err);
            })
        },
        notAllowed(menuItemId, subMenuId = null) {
            if (menuItemId in this.clientMenuItemPolicies && this.clientMenuItemPolicies[menuItemId].read_only) return false
            if (menuItemId in this.systemMenuItemPolicies && this.systemMenuItemPolicies[menuItemId].read_only) return false
            if (menuItemId in this.clientMenuItemPolicies) return this.clientMenuItemPolicies[menuItemId].not_allowed
            if (menuItemId in this.systemMenuItemPolicies) return this.systemMenuItemPolicies[menuItemId].not_allowed
            if (subMenuId in this.clientMenuItemPolicies) return this.clientMenuItemPolicies[subMenuId].not_allowed
            if (subMenuId in this.systemMenuItemPolicies) return this.systemMenuItemPolicies[subMenuId].not_allowed
        },
        readOnly(menuItemId, subMenuId = null) {
            if (subMenuId) {
                if (subMenuId in this.clientMenuItemPolicies && !this.clientMenuItemPolicies[subMenuId].read_only) return false
                if (subMenuId in this.systemMenuItemPolicies && !this.systemMenuItemPolicies[subMenuItemId].read_only) return false
            }
            if (menuItemId in this.clientMenuItemPolicies) return this.clientMenuItemPolicies[menuItemId].read_only
            if (menuItemId in this.systemMenuItemPolicies) return this.systemMenuItemPolicies[menuItemId].read_only
            return true
        },
        fullAccess(menuItemId, subMenuId = null) {
            if (subMenuId) {
                if (subMenuId in this.clientMenuItemPolicies && !this.clientMenuItemPolicies[subMenuId].full_access) return false
                if (subMenuId in this.systemMenuItemPolicies && !this.systemMenuItemPolicies[subMenuItemId].full_access) return false
            }
            if (menuItemId in this.clientMenuItemPolicies) return this.clientMenuItemPolicies[menuItemId].full_access
            if (menuItemId in this.systemMenuItemPolicies) return this.systemMenuItemPolicies[menuItemId].full_access
            return true
        },
        saveNewUserType() {
            if (!this.newUserType.user_type_code || !this.newUserType.description) { return }
            this.modalMessage = 'Saving new user type...'
            this.apiCall({ destination: 'save_item_user_type', data: this.newUserType }).then(obj => {
                this.modalMessage = obj.message
                if (obj.status == 'success') {
                    this.showModal = false
                    this.newUserType = {}
                    this.userTypes.push(obj.user_type)
                    this.selectedUserTypeCode = obj.user_type.user_type_code
                    this.fetchUserTypePermissions()
                }
            }).catch(err => {
                console.log(err);
            })
        },
        toggleNotAllowed(menuItemId, menuItems = null, permissions = null) {
            if (!menuItemId) return
            if (this.getAdministratorFlag) {
                if (!permissions) {
                    if (menuItemId in this.systemMenuItemPolicies) {
                        permissions = { not_allowed: !this.systemMenuItemPolicies[menuItemId].not_allowed }
                    } else {
                        permissions = { not_allowed: true }
                    }
                }
                this.$set(this.systemMenuItemPolicies, menuItemId, { ...this.systemMenuItemPolicies[menuItemId], ...permissions })
            } else {
                // don't allow _LITE users to overwrite system defaults
                if (this.getUser.user_type_code.indexOf('_LITE') > -1) {
                    if (menuItemId in this.systemMenuItemPolicies) {
                        return
                    }
                }
                if (!permissions) {
                    if (menuItemId in this.clientMenuItemPolicies) {
                        permissions = { not_allowed: !this.clientMenuItemPolicies[menuItemId].not_allowed }
                    } else {
                        if (menuItemId in this.systemMenuItemPolicies) {
                            permissions = { not_allowed: false }
                        } else {
                            permissions = { not_allowed: true }
                        }
                    }
                }
                this.$set(this.clientMenuItemPolicies, menuItemId, { ...this.clientMenuItemPolicies[menuItemId], ...permissions })
            }
            if (menuItems && Object.keys(menuItems).length > 0) {
                for (let item in menuItems) {
                    this.toggleNotAllowed(menuItems[item].menu_item_id, null, permissions)
                }
            }
        },
        togglePermissions(menuItemId, menuItems = null, permissions = null) {
            if (!permissions) {
                if (menuItemId in this.clientMenuItemPolicies) {
                    if (this.clientMenuItemPolicies[menuItemId].not_allowed) {
                        permissions = { not_allowed: false, full_access: true }
                    } else if (this.clientMenuItemPolicies[menuItemId].full_access) {
                        permissions = { read_only: true, full_access: false }
                    } else {
                        permissions = { read_only: false, full_access: false, not_allowed: true }
                    }
                } else {
                    permissions = { not_allowed: true }
                }
            }
            this.$set(this.clientMenuItemPolicies, menuItemId, { ...this.clientMenuItemPolicies[menuItemId], ...permissions })
            if (menuItems && Object.keys(menuItems).length > 0) {
                for (let item in menuItems) {
                    this.togglePermissions(menuItems[item].menu_item_id, null, permissions)
                }
            }
        },
        toggleSubMenuNotAllowed(menuItemId) {
            this.$set(this.clientMenuItemPolicies, menuItemId, { ...this.clientMenuItemPolicies[menuItemId], not_allowed: !this.notAllowed(menuItemId) })
        },
        toggleReadOnly(menuItemId) {
            let readOnly = menuItemId in this.clientMenuItemPolicies && this.clientMenuItemPolicies[menuItemId].read_only
            this.$set(this.clientMenuItemPolicies, menuItemId, { ...this.clientMenuItemPolicies[menuItemId], read_only: !readOnly })
        },
        toggleFullAccess(menuItemId) {
            let fullAccess = menuItemId in this.clientMenuItemPolicies && this.clientMenuItemPolicies[menuItemId].full_access
            this.$set(this.clientMenuItemPolicies, menuItemId, { ...this.clientMenuItemPolicies[menuItemId], full_access: !fullAccess })
        },
        toggleSubMenuItemNotAllowed(menuItemId, subMenuItemId) {
            let notAllowed = !this.notAllowed(menuItemId, subMenuItemId)
            this.$set(this.clientMenuItemPolicies, menuItemId, { ...this.clientMenuItemPolicies[menuItemId], not_allowed: notAllowed })
            if (notAllowed) {
                this.$set(this.clientMenuItemPolicies, menuItemId, { ...this.clientMenuItemPolicies[menuItemId], read_only: false, full_access: false })
            }
        },
        saveUserTypePermissions() {
            this.selectNotAllowed = false
            let postData = {
                user_type_code: this.selectedUserTypeCode
            }
            if (this.getAdministratorFlag) {
                postData.menu_item_user_type_policies = this.systemMenuItemPolicies
            } else {
                postData.menu_item_user_type_policies = this.clientMenuItemPolicies
            }
            this.savingUserTypePermissions = true
            this.modalMessage = 'Saving user type permissions...'
            this.showModal = true
            this.apiCall({ destination: 'save_list_menu_item_user_type_policies', data: postData }).then(obj => {
                this.modalMessage = obj.message || 'User type permissions saved!'
                if (obj.status == 'success') {
                    // timeout for 2 seconds to show the message, then close the modal and refresh the permissions
                    setTimeout(() => {
                        this.showModal = false
                        this.savingUserTypePermissions = false
                    }, 2000) // 2 seconds timeout
                    if (this.getAdministratorFlag) {
                        this.systemMenuItemPoliciesCopy = this.$copy(this.systemMenuItemPolicies)
                    } else {
                        this.clientMenuItemPoliciesCopy = this.$copy(this.clientMenuItemPolicies)
                    }
                    this.fetchUserTypePermissions()
                }
            }).catch(err => {
                console.log(err);
                this.savingUserTypePermissions = false
            })
        },
        saveUserPermissions() {
            this.selectNotAllowed = false
            this.apiCall({ destination: 'save_list_menu_item_user_policies', data: { user_id: this.selectedUser.user_id, client_menu_item_user_policies: this.clientMenuItemPolicies }}).then(obj => {
                if (obj.status == 'success') {
                    this.clientMenuItemPoliciesCopy = this.$copy(this.clientMenuItemPolicies)
                    this.fetchUserPermissions()
                } else {
                    this.message = obj.message
                }
            }).catch(err => {
                console.log(err);
            })
        },
        cancelUserTypePermissions() {
            this.clientMenuItemPolicies = this.$copy(this.clientMenuItemPoliciesCopy)
            this.systemMenuItemPolicies = this.$copy(this.systemMenuItemPoliciesCopy)
            this.selectNotAllowed = false
        },
        resetPermissions() {
            let data = {}
            if (this.selectedUserTypeCode) {
                data.user_type_code = this.selectedUserTypeCode
            } else {
                data.user_id = this.selectedUser.user_id
            }
            this.showResetUserTypePermissionsWarning = false
            this.apiCall({ destination: 'reset_menu_item_policy_defaults', data: data }).then(obj => {
                this.loading = false
                if (obj.status == 'success') {
                    if (!this.selectedUserTypeCode) this.fetchUserPermissions()
                    else this.fetchUserTypePermissions()
                } else {
                    this.message = obj.message
                }
            }).catch(err => {
                this.loading = false
                console.log(err);
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.list-view {
    grid-gap: 2em;
}
.link {
    text-decoration: underline;
    color: #0d7ea0;
    cursor: pointer;
    &.disabled {
        background: none;
        color: #999;
    }
}
#dashboard_module .dashboard-flight-deck {
    grid-template-columns: 1fr;
}
.first-row, .second-row {
    display: grid;
    grid-gap: 10px;
}
.first-row {
    grid-template-columns: 19px 1fr 19px 1fr;
}
.second-row {
    grid-template-columns: 19px 1fr 19px 1fr 19px 1fr 19px 1fr;
}
ul.user-list {
    margin: 0 0 0.5em 0.5em;
    list-style: none;
    li {
        padding: 0.25em;
        cursor: pointer;
        &.user div {
            padding-left: 1rem;
        }
        &.user button {
            font-size: 0.6em;
            width: initial;
        }
    }
    li:hover { background-color: #e4e4e4;}
}
#user-permission-filters {
    position: fixed;
    top: 8em; left: 0;
    width: 280px;
    padding: 1em 1em 2em 1em;
    height: calc(100vh - 9em);
    overflow-y: scroll;
    // .filter-list-button-large {
    //     margin: 0;
    // }
}

.user-permission-content {
	position: fixed;
	top: 7.5em; left: 300px; width: calc(100% - 300px);
	margin: 0;
	padding: 0 1em 2em 0;
	// set height to fill space between header and footer
	height: calc(100vh - 7rem);
    overflow-y: scroll;
}

.flight-deck-nav-pane {
    position: relative;
    top: initial !important;
    left: initial !important;
    box-shadow: none;
    z-index: 1;
    border-bottom: none;
    padding: 1em 0 0 0;
    .flight-deck-nav-pane-columns {
        height: auto;
        overflow: auto;
    }
    ul.columns { min-height: auto; }
}
.far {
    font-size: 1.3em;
}
.fa-square {
    color: #CCC; margin: 0 0.5em 0 0;
}
.fa-check-square {
    color: #0d7ea0; margin: 0 0.5em 0 0;
}
.not_allowed { color: #999; }
.advanced-user-permissions {
    font-size: 1.2em;
    ul {
        list-style: none;

        li {
            margin-bottom: 2rem;

            div {
                margin-bottom: 0.5rem;
            }
        }

        .title {
            font-weight: bold;
        }

        .indent-1 {
            padding-left: 1rem;
        }
        div.height-23 {
            height: 23px;
        }
        i {
            display: inline-block;
            cursor: pointer;
            color: #999;
            width: 24px;
            height: 19px;
        }
        span.not-allowed {
            display: inline-block;
            position: relative;
            width: 24px;
            height: 18px;
            i {
                position: absolute;
            }
        }
        i.enabled {
            color: initial;
        }
        i.disabled {
            color: #999;
            background: none;
            cursor: not-allowed;
        }
    }
}
@media (min-width: 768px) {
    ul.columns {
        column-count: 3;
    }
}
</style>
